import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../login/axiosInstance';
import { errorMessage } from '../login/Login';
import { prepareToolSession } from './appLaunch';
import { appNames } from './config';

export default function EmbeddedTool() {
  const { appId } = useParams(), navigate = useNavigate(), container = useRef(null);
  const [state, setState] = useState({ loading: true, error: '' }), [attempt, setAttempt] = useState(0);
  const directory = useQuery({ queryKey: ['tools'], queryFn: async () => (await api.get('/api/tools', { headers: { 'X-Breck-Portal-Origin': window.location.origin } })).data });
  const tool = directory.data?.find(item => item.id === appId);
  useEffect(() => {
    document.documentElement.dataset.toolsApp = appId;
    return () => { delete document.documentElement.dataset.toolsApp; };
  }, [appId]);
  useEffect(() => {
    if (!tool?.enabled) return;
    const controller = new AbortController(), host = container.current;
    setState({ loading: true, error: '' });
    const receive = event => {
      if (event.origin !== new URL(tool.url).origin || event.source !== host.querySelector('iframe')?.contentWindow) return;
      if (event.data?.type === 'breck:tools:home') navigate('/');
      if (event.data?.type === 'breck:theme' && ['light', 'dark'].includes(event.data.theme)) {
        // The child owns the visible theme toggle; keep Android's outer safe area in the same mode.
        const root = document.documentElement;
        root.dataset.theme = event.data.theme; root.style.colorScheme = event.data.theme;
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.content = getComputedStyle(root).getPropertyValue('--tools-edge-background').trim()
          || (event.data.theme === 'dark' ? '#17191d' : '#f7f8f6');
      }
    };
    window.addEventListener('message', receive);
    prepareToolSession(tool, async challenge => (await api.post('/api/tools/launch', { app: tool.id, challenge }, { signal: controller.signal })).data.url, controller.signal, { container: host })
      .then(() => { if (!controller.signal.aborted) setState({ loading: false, error: '' }); })
      .catch(error => { if (!controller.signal.aborted) setState({ loading: false, error: error.response ? errorMessage(error) : error.message }); });
    return () => { controller.abort(); window.removeEventListener('message', receive); host.replaceChildren(); };
  }, [tool, attempt, navigate]);
  const error = directory.error ? errorMessage(directory.error) : directory.data && !tool?.enabled ? 'Access to this app is disabled or unavailable. Contact an administrator.' : state.error;
  return <section className="tools-workspace" aria-label={`${appNames[appId] || 'App'} workspace`}>
    {error ? <div className="tools-workspace-notice" role="alert"><p>{error}</p>{tool?.enabled && <button className="button secondary" onClick={() => setAttempt(value => value + 1)}>Try again</button>} <Link className="button secondary" to="/">All tools</Link></div>
      : state.loading && <div className="tools-workspace-notice" role="status">Opening {appNames[appId] || 'app'}…</div>}
    <div className="tools-frame-host" ref={container} />
  </section>;
}
